<template>
  <div class="flex flex-col gap-6 z-1000 w-full h-full">
    <LoadingOverlay v-if="loading" :value="loading" />

    <div v-else class="flex w-full items-start justify-center">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div v-if="!userView" class="flex items-center gap-2">
            <el-tooltip
              v-if="uuid && uuid !== 'new'"
              :content="$t('Режим просмотра')"
              placement="top"
            >
              <Button variant="outline" size="icon" @click="goToView">
                <svg-icon name="huge-eye" class="w-4 h-4" />
              </Button>
            </el-tooltip>

            <Button variant="default" @click="submitForm" :disabled="loader">
              {{ $t("Сохранить") }}
            </Button>
          </div>

          <div v-else class="flex items-center gap-3">
            <Button variant="outline" size="icon" @click="goToEdit">
              <svg-icon name="huge-edit" class="w-4 h-4" />
            </Button>
          </div>
        </template>
      </BaseTabs>
    </div>

    <component
      :is="currentTabComponent"
      :form="form"
      :roles="roles"
      :organizations="organizations"
      :selected-user="selectedUser"
      ref="activeForm"
      class="flex-1 w-full h-full"
    />
  </div>
</template>

<script>
import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import Button from "@/shared/ui/button/Button.vue";

import DataUser from "./tabs/DataUser.vue";
import UserHistory from "./tabs/UserHistory.vue";
import UserSessions from "./tabs/UserSessions.vue";
import UserSettingRight from "./tabs/UserSettingRight.vue";

import { createUser, updateUser, getUserItem } from "@/entities/user";

export default {
  name: "UserItem",

  components: {
    LoadingOverlay,
    BaseTabs,
    Button,
    DataUser,
    UserAssignedRight: UserSettingRight,
    UserHistory,
    UserSessions,
  },

  props: {
    roles: {
      type: Array,
      default: () => [],
    },
    organizations: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      loader: false,
      activeTab: "data",

      selectedUser: null,

      form: {
        last_name: "",
        first_name: "",
        middle_name: "",
        identifier: "",
        email: "",
        phone: null,
        organization_id: null,
        role_codes: [],
      },
    };
  },

  async created() {
    if (this.uuid && this.uuid !== "new") {
      await this.getUserItem();
    }
  },

  computed: {
    uuid() {
      return this.$route.params.id;
    },

    userView() {
      return this.$route.name === "user_item_view";
    },

    tabs() {
      const tabs = {
        data: {
          label: this.$t("Данные пользователя"),
          icon: "huge-text-config",
        },
      };

      if (this.userView) {
        tabs.history = {
          label: this.$t("История"),
          icon: "huge-history",
        };

        tabs.active_sessions = {
          label: this.$t("Активные сессии"),
          icon: "huge-unlock",
        };
      }

      return tabs;
    },

    currentTabComponent() {
      const map = {
        data: DataUser,
        setting_right: UserSettingRight,
        history: UserHistory,
        active_sessions: UserSessions,
      };

      return map[this.activeTab] || DataUser;
    },
  },

  watch: {
    "$route.name"() {
      if (this.uuid && this.uuid !== "new") {
        this.getUserItem();
      }
      if (!this.tabs[this.activeTab]) {
        const fallbackTab = Object.keys(this.tabs)[0];
        this.activeTab = fallbackTab;
        this.changeTab(fallbackTab);
      }
    },

    "$route.query.tab": {
      immediate: true,
      handler(tab) {
        this.activeTab = tab && this.tabs[tab] ? tab : "data";
      },
    },
  },

  methods: {
    async getUserItem() {
      this.loading = true;

      try {
        const data = await getUserItem(this.uuid);

        if (data?.success && data?.payload) {
          this.selectedUser = data.payload;

          this.form = {
            last_name: this.selectedUser.last_name || "",
            first_name: this.selectedUser.first_name || "",
            middle_name: this.selectedUser.middle_name || "",
            identifier: this.selectedUser.identifier || "",
            email: this.selectedUser.email || "",
            login: this.selectedUser.email || "",
            phone: this.selectedUser.phone || null,
            organization_id: this.selectedUser.organization_id,
            organization: this.selectedUser.organization || null,
            department: this.selectedUser.department || null,
            role_codes: this.selectedUser.roles?.map((i) => i.code) || [],
          };
        }
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },

    changeTab(tab) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab,
        },
      });
    },

    goToView() {
      this.$router.push({
        name: "user_item_view",
        params: { id: this.uuid },
      });
    },

    goToEdit() {
      this.$router.push({
        name: "user_item",
        params: { id: this.uuid },
      });
    },

    async submitForm() {
      const form = this.$refs.activeForm;

      if (!form?.validate) return;

      const valid = await form.validate();

      if (!valid) {
        console.warn("❌ Validation failed");
        return;
      }

      if (this.uuid && this.uuid !== "new") {
        await this.updateUser();
      } else {
        await this.createUser();
      }
    },

    async createUser() {
      this.loader = true;

      try {
        const data = await createUser(this.form);

        if (data?.success && data?.payload) {
          this.$router.push({
            name: "user_item_view",
            params: { id: data.payload.uuid },
          });
        }
      } finally {
        this.loader = false;
      }
    },

    async updateUser() {
      this.loader = true;

      try {
        const data = await updateUser(this.uuid, this.form);

        if (data?.success) {
          this.$router.push({
            name: "user_item_view",
            params: { id: this.uuid },
          });
        }
      } finally {
        this.loader = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.button {
  @apply h-10 flex justify-center items-center p-4 border rounded outline-none;

  &:active {
    transform: scale(0.98);
  }
}

.menu {
  @apply w-[282px] flex flex-col p-2 gap-2 rounded-xl outline outline-1 outline-offset-[-1px];
}

.item {
  @apply flex items-center px-3 py-2 gap-3 rounded-lg cursor-pointer transition-colors;

  svg {
    &.danger {
      @apply text-red-500;
    }
  }

  span {
    @apply text-base font-medium leading-6;

    &.danger {
      @apply text-red-500;
    }
  }
}
</style>
